import React, {useRef, useState} from 'react'
import {ColumnDef, flexRender, getCoreRowModel, Table, useReactTable} from '@tanstack/react-table'
import moment from 'moment'
import QrCodeScanner from '../../../components/QrCodeScanner/QrCodeScanner'
import {getKarticaRolne} from '../../../api/karticaRolne'
import {karticaRolneModel} from '../../../models/KarticaRolneModel'
import KarticaRolnePdfButton from './KarticaRolnePdfButton'

const displayValue = (value: React.ReactNode) =>
  value === null || value === undefined || value === '' ? '—' : value

const formatDate = (value?: string | null, withTime = false) => {
  if (!value) return '—'
  const date = moment(value, moment.ISO_8601, true)
  return date.isValid() ? date.format(withTime ? 'DD.MM.YYYY. HH:mm' : 'DD.MM.YYYY.') : '—'
}

type Detail = {label: string; value: React.ReactNode}

const DetailsCard = ({title, icon, items}: {title: string; icon: string; items: Detail[]}) => (
  <section className='card shadow-sm h-100'>
    <div className='card-header border-bottom py-4'>
      <h2 className='card-title fs-5 fw-bold gap-3 m-0'>
        <i className={`bi ${icon} text-primary fs-3`} aria-hidden='true' />
        {title}
      </h2>
    </div>
    <div className='card-body p-5 p-lg-6'>
      <dl className='mb-0'>
        {items.map(({label, value}, index) => (
          <div key={label} className={`row g-2 py-3 ${index ? 'border-top' : ''}`}>
            <dt className='col-6 text-muted fw-normal'>{label}</dt>
            <dd className='col-6 text-end text-gray-900 fw-semibold text-break mb-0'>
              {displayValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  </section>
)

function HistorySection<T>({title, icon, table}: {title: string; icon: string; table: Table<T>}) {
  const rows = table.getRowModel().rows
  return (
    <section className='card shadow-sm'>
      <div className='card-header border-bottom py-4'>
        <h2 className='card-title fs-5 fw-bold gap-3 m-0'>
          <i className={`bi ${icon} text-primary fs-3`} aria-hidden='true' />
          {title}
          <span className='badge badge-light-primary'>{rows.length}</span>
        </h2>
      </div>
      <div className='card-body p-5 p-lg-6'>
        {rows.length === 0 ? (
          <div className='text-muted text-center py-6'>Nema evidentiranih zapisa.</div>
        ) : (
          <>
            <div className='d-md-none d-flex flex-column gap-4'>
              {rows.map((row) => (
                <dl key={row.id} className='border rounded p-4 mb-0'>
                  {row.getVisibleCells().map((cell) => (
                    <div key={cell.id} className='row g-2 py-2'>
                      <dt className='col-5 text-muted fw-normal'>
                        {String(cell.column.columnDef.header)}
                      </dt>
                      <dd className='col-7 text-end fw-semibold text-break mb-0'>
                        {cell.getValue() == null || cell.getValue() === ''
                          ? '—'
                          : flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </dd>
                    </div>
                  ))}
                </dl>
              ))}
            </div>
            <div className='d-none d-md-block table-responsive'>
              <table className='table align-middle table-row-dashed gy-4 gs-3 mb-0'>
                <caption className='visually-hidden'>{title}</caption>
                <thead>
                  {table.getHeaderGroups().map((group) => (
                    <tr key={group.id} className='text-muted fw-bold fs-7 bg-light'>
                      {group.headers.map((header) => (
                        <th key={header.id} scope='col' className='text-nowrap'>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {cell.getValue() == null || cell.getValue() === ''
                            ? '—'
                            : flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  )
}

const prometColumns: ColumnDef<karticaRolneModel['promet'][number]>[] = [
  {header: 'ID', accessorKey: 'Id'},
  {header: 'Datum', accessorKey: 'Datum', cell: ({row}) => formatDate(row.original.Datum)},
  {header: 'Nalog', accessorKey: 'BrojDokumentaIzlaza'},
  {header: 'Početno', accessorKey: 'Pocetno'},
  {header: 'Krajnje', accessorKey: 'Krajnje'},
  {header: 'Promet', accessorKey: 'Promet'},
  {header: 'Uposlenik', accessorKey: 'uposlenik.UposlenikImePrezime'},
]
const lokacijeColumns: ColumnDef<karticaRolneModel['lokacije'][number]>[] = [
  {header: 'ID', accessorKey: 'Id'},
  {header: 'Rolna', accessorKey: 'RolnaID'},
  {header: 'Iz lokacije', accessorKey: 'IzLokacije'},
  {header: 'U lokaciju', accessorKey: 'ULokaciju'},
  {
    header: 'Inventura',
    accessorKey: 'Inventura',
    cell: ({getValue}) => (Number(getValue()) === 1 ? 'Da' : 'Ne'),
  },
  {header: 'Datum', accessorKey: 'Datum', cell: ({row}) => formatDate(row.original.Datum, true)},
  {header: 'Uposlenik', accessorKey: 'uposlenik.UposlenikImePrezime'},
]
const emptyRows: never[] = []

const KarticaRolne = () => {
  const [rolnaId, setRolnaId] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<karticaRolneModel>()
  const [error, setError] = useState('')
  const [searched, setSearched] = useState(false)
  const requestId = useRef(0)
  const rolna = data?.rolnaInfo

  const loadRoll = async (value: string) => {
    const id = value.split(';')[0].trim()
    if (!id) return
    const currentRequest = ++requestId.current
    setRolnaId(id)
    setLoading(true)
    setError('')
    setData(undefined)
    setSearched(true)
    try {
      const response = await getKarticaRolne({rolnaId: id})
      if (currentRequest === requestId.current) setData(response.data)
    } catch (error) {
      if (currentRequest === requestId.current) {
        setError(
          error instanceof Error && error.message
            ? error.message
            : 'Učitavanje nije uspjelo. Pokušajte ponovo.'
        )
      }
    } finally {
      if (currentRequest === requestId.current) setLoading(false)
    }
  }
  const prometTable = useReactTable({
    data: data?.promet ?? emptyRows,
    columns: prometColumns,
    getCoreRowModel: getCoreRowModel(),
  })
  const lokacijeTable = useReactTable({
    data: data?.lokacije ?? emptyRows,
    columns: lokacijeColumns,
    getCoreRowModel: getCoreRowModel(),
  })
  const atesti = [
    ...(data?.atestLinks ?? []).map((url, index) => ({url, label: `Atest ${index + 1}`})),
    ...(data?.atestProizvodjacLinks ?? []).map((url, index) => ({
      url,
      label: `Atest proizvođača ${index + 1}`,
    })),
  ]
  const sarza =
    Number.parseFloat(rolna?.stavka?.ugovor?.BrojUgovora ?? '') /
    Number.parseFloat(rolna?.stavka?.ManualId ?? '')

  return (
    <div className='d-flex flex-column gap-6 mb-10'>
      <section className='card shadow-sm border-top border-primary border-3'>
        <div className='card-body p-5 p-lg-8'>
          <div className='text-muted fw-semibold fs-7 mb-2'>Repromaterijali</div>
          <h1 className='fs-2 fw-bold mb-2'>Kartica rolne</h1>
          <p className='text-muted mb-6'>
            Skenirajte QR kod ili unesite broj rolne za pregled podataka.
          </p>
          <div className='row g-5 align-items-end'>
            <div className='col-12 col-lg-4'>
              <div className='form-label fw-bold'>Brzi pregled</div>
              <QrCodeScanner
                buttonLabel='Skeniraj QR kod'
                buttonClassName='btn-lg w-100'
                onScan={loadRoll}
              />
            </div>
            <div className='col-12 col-lg-8'>
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  loadRoll(rolnaId)
                }}
              >
                <label htmlFor='kartica-rolna-id' className='form-label fw-bold'>
                  Broj rolne
                </label>
                <div className='d-flex flex-column flex-sm-row gap-3'>
                  <input
                    id='kartica-rolna-id'
                    type='text'
                    inputMode='numeric'
                    enterKeyHint='search'
                    autoComplete='off'
                    placeholder='Unesite broj rolne'
                    value={rolnaId}
                    className='form-control form-control-lg form-control-solid'
                    onChange={(event) => setRolnaId(event.target.value)}
                  />
                  <button
                    type='submit'
                    className='btn btn-primary btn-lg flex-shrink-0'
                    disabled={loading || !rolnaId.trim()}
                  >
                    <i className='bi bi-search me-2' aria-hidden='true' />
                    Prikaži karticu
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      {loading ? (
        <div className='card shadow-sm p-8 text-center' role='status'>
          <div>
            <span className='spinner-border text-primary mb-4' aria-hidden='true' />
          </div>
          Učitavanje kartice rolne…
        </div>
      ) : error ? (
        <div className='alert alert-danger mb-0' role='alert'>
          <div className='fw-bold mb-1'>Kartica nije učitana</div>
          {error}
        </div>
      ) : !rolna ? (
        <div className='card shadow-sm p-8 p-lg-12 text-center' role='status'>
          <i className='bi bi-upc-scan fs-3x text-primary mb-4' aria-hidden='true' />
          <h2 className='fs-5'>{searched ? 'Rolna nije pronađena' : 'Pronađite rolnu'}</h2>
          <p className='text-muted mb-0'>
            {searched
              ? 'Provjerite broj rolne i pokušajte ponovo.'
              : 'Kamera očitava QR kod, a broj rolne možete unijeti i ručno.'}
          </p>
        </div>
      ) : (
        <>
          <section className='card shadow-sm' aria-label='Pregled rolne'>
            <div className='card-body p-5 p-lg-8'>
              <div className='d-flex flex-wrap align-items-start justify-content-between gap-4 mb-6'>
                <div>
                  <div className='text-muted fs-7 mb-1'>Broj rolne</div>
                  <h2 className='fs-2 fw-bold mb-2'>Rolna #{rolna.ReprMatId}</h2>
                  <div className='text-muted text-break'>
                    Serijski broj:{' '}
                    <span className='text-gray-900 fw-semibold'>
                      {displayValue(rolna.ReprMatSeriski)}
                    </span>
                  </div>
                </div>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                  <span className='badge badge-light-primary fs-6 px-4 py-3 text-wrap'>
                    {rolna.status?.Naziv || 'Status nije upisan'}
                  </span>
                  {data && <KarticaRolnePdfButton key={rolna.ReprMatId} data={data} />}
                </div>
              </div>
              <div className='row g-4'>
                {[
                  {label: 'Trenutna težina', value: rolna.ReprMatTrenutnaKolicina},
                  {
                    label: 'Lokacija / red',
                    value:
                      [rolna.ReprMatLokacija, rolna.ReprMatLokacija2]
                        .filter((v) => v !== null && v !== undefined && v !== '')
                        .join(' / ') || 'Nije upisana lokacija',
                  },
                  {label: 'Datum ulaza', value: formatDate(rolna.ReprMatUlazDatum)},
                  {label: 'Datum rascarinjenja', value: formatDate(rolna.ReprMatDatumCarinjenja)},
                ].map(({label, value}) => (
                  <div className='col-12 col-sm-6 col-xl-3' key={label}>
                    <div className='bg-light rounded p-5 h-100'>
                      <div className='text-muted fs-7 mb-2'>{label}</div>
                      <div className='fw-bold fs-5 text-break'>{displayValue(value)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <div className='row g-6'>
            <div className='col-12 col-md-6 col-xl-4'>
              <DetailsCard
                title='Materijal i dimenzije'
                icon='bi-layers'
                items={[
                  {label: 'Materijal', value: rolna.materijal?.MetaValue},
                  {label: 'Kvalitet', value: rolna.kvalitet?.MatKvalitetNaziv},
                  {label: 'Zaštita', value: rolna.zastita?.MetaValue},
                  {label: 'Kategorija', value: rolna.kategorija?.KatNaziv},
                  {label: 'Debljina', value: rolna.ReprMatDebljina},
                  {label: 'Širina', value: rolna.ReprMatSirina},
                  {label: 'Dužina', value: rolna.ReprMatDuzina},
                  {label: 'Netto', value: rolna.ReprMatNettoKolicina},
                  {label: 'Brutto', value: rolna.ReprMatBruttoKolicina},
                ]}
              />
            </div>
            <div className='col-12 col-md-6 col-xl-4'>
              <DetailsCard
                title='Boje i površinska obrada'
                icon='bi-palette'
                items={[
                  {label: 'Kod boje', value: rolna.kod_boje?.KodBojeNaziv},
                  {label: 'Boja lica', value: rolna.boja_lica?.BojaNaziv},
                  {label: 'Struktura A', value: rolna.struktura_a?.MetaValue},
                  {label: 'Boja A', value: rolna.boja_a?.MetaValue},
                  {label: 'Boja naličja', value: rolna.boja_nalicja?.BojaNaziv},
                  {label: 'Struktura B', value: rolna.struktura_b?.MetaValue},
                  {label: 'Boja B', value: rolna.boja_b?.MetaValue},
                ]}
              />
            </div>
            <div className='col-12 col-xl-4'>
              <DetailsCard
                title='Prijem i porijeklo'
                icon='bi-box-seam'
                items={[
                  {label: 'Ulaz', value: rolna.ReprMatUlazBroj},
                  {label: 'Skladište', value: rolna.skladiste?.SkladisteNaziv},
                  {label: 'Proizvođač', value: rolna.proizvodjac?.PartnerNaziv},
                  {label: 'Dobavljač', value: rolna.dobavljac?.PartnerNaziv},
                  {
                    label: 'Stavka / Manual ID',
                    value: rolna.stavka
                      ? `${rolna.stavka.Id ?? '—'} / ${rolna.stavka.ManualId ?? '—'}`
                      : null,
                  },
                  {label: 'Ugovor', value: rolna.stavka?.ugovor?.BrojUgovora},
                  {label: 'Šarža', value: Number.isFinite(sarza) ? sarza : null},
                  {label: 'Garancija', value: rolna.garancija?.MetaValue},
                  {
                    label: 'Datum popravke (inventura)',
                    value: formatDate(rolna.ReprMatPopravljeno, true),
                  },
                ]}
              />
            </div>
          </div>
          {atesti.length > 0 && (
            <section className='card shadow-sm'>
              <div className='card-body p-5 p-lg-6'>
                <h2 className='fs-5 fw-bold mb-2'>Atesti i dokumentacija</h2>
                <p className='text-muted fs-7 mb-5'>
                  Atesti uključuju povezane stavke nastale iz originalne stavke.
                </p>
                <div className='d-flex flex-column flex-sm-row flex-wrap gap-3'>
                  {atesti.map(({url, label}) => (
                    <a
                      key={label}
                      href={url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn btn-light-primary text-start'
                    >
                      <i className='bi bi-file-earmark-pdf fs-3 me-2' aria-hidden='true' />
                      {label}
                      <span className='visually-hidden'> (otvara se u novoj kartici)</span>
                    </a>
                  ))}
                </div>
              </div>
            </section>
          )}
          <HistorySection title='Promet' icon='bi-arrow-left-right' table={prometTable} />
          <HistorySection title='Izmjene lokacije' icon='bi-geo-alt' table={lokacijeTable} />
        </>
      )}
    </div>
  )
}

export default KarticaRolne
